import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { AdminOrderServices } from "../../services/admin/AdminOrderServices";
import { useNotification } from "../../context/NotificationContext";
import { ChevronLeft, User, MapPin, Package, Loader2, CalendarClock, Disc, Box, CheckCircle } from "lucide-react";
import { formatRelativeTime } from "../../utils/dateUtils";


export default function OrderDetailsPage() {
    const { id } = useParams();
    const { showNotification } = useNotification();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updatingParams, setUpdatingParams] = useState(null); // Track which item ID is updating

    const fetchOrderDetails = useCallback(async () => {
        try {
            setLoading(true);
            const res = await AdminOrderServices.getOrderDetails(id);
            if (res.status === "success") {
                setOrder(res.data);
            }
        } catch (error) {
            console.error("Failed to fetch order:", error);
            showNotification({ type: "failed", message: "Failed to load order details" });
        } finally {
            setLoading(false);
        }
    }, [id, showNotification]);

    useEffect(() => {
        fetchOrderDetails();
    }, [fetchOrderDetails]);

    const handleUpdateItemStatus = async (itemId, newStatus) => {
        try {
            setUpdatingParams(itemId);
            const res = await AdminOrderServices.updateOrderItemStatus(itemId, newStatus);
            if (res.status === "success") {
                showNotification({ type: "success", message: res.message });
                fetchOrderDetails(); // Refresh to catch overall order status sync
            }
        } catch (error) {
            showNotification({ type: "failed", message: "Failed to update item status" });
        } finally {
            setUpdatingParams(null);
        }
    };

    const getStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case "completed":
                return <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Completed</span>;
            case "shipped":
                return <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Shipped</span>;
            case "processing":
                return <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Processing</span>;
            case "paid":
                return <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Paid (Processing)</span>;
            default:
                return <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">{status}</span>;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="animate-spin text-jazz-brown w-12 h-12" />
            </div>
        );
    }

    if (!order) {
        return (
            <div className="p-6 text-center text-black dark:text-white/50 font-medium">Order not found.</div>
        );
    }

    const hasPhysicalItems = order.items.some(item => item.category_type !== "Music Content");

    return (
        <div className="p-6 lg:p-10 max-w-7xl mx-auto min-h-screen bg-[#FAFAFA] dark:bg-[#111]">
            <Link to="/admin/orders" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-black hover:opacity-70 dark:text-white/60 dark:hover:text-white transition-opacity mb-8">
                <ChevronLeft size={16} /> Back to Orders
            </Link>

            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-bold font-serif text-black dark:text-white mb-2">Order {order.reference}</h1>
                    <div className="flex items-center gap-4 text-sm text-black dark:text-white/50">
                        <span className="flex items-center gap-2 font-medium">
                            <CalendarClock size={16} />
                            {new Date(order.created_at).toLocaleString()} ({formatRelativeTime(order.created_at)})
                        </span>
                    </div>
                </div>
                <div>
                    <div className="flex flex-col items-end gap-2">
                        <span className="text-xs font-bold uppercase tracking-widest text-black dark:text-white/50">Overall Status</span>
                        {getStatusBadge(order.status)}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left: Items List */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-black/5 shadow-sm">
                        <h2 className="text-lg font-bold font-serif mb-6 flex items-center gap-2">
                            <Package size={20} className="text-jazz-brown" />
                            Order Items
                        </h2>
                        <div className="space-y-6">
                            {order.items.map(item => {
                                const isDigital = item.category_type === "Music Content";
                                return (
                                    <div key={item.id} className="flex flex-col sm:flex-row gap-4 p-4 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222]">
                                        <div className="flex-1">
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <h3 className="text-sm font-bold text-black dark:text-white flex items-center gap-2">
                                                        {isDigital ? <Disc size={16} className="text-jazz-brown" /> : <Box size={16} className="text-black" />}
                                                        {item.product_name}
                                                    </h3>
                                                    <p className="text-xs text-black dark:text-white/50 mt-1 uppercase tracking-wider font-bold">
                                                        {isDigital ? "Digital Download" : "Physical Merchandise"}
                                                    </p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm font-bold font-serif">${item.subtotal.toFixed(2)}</p>
                                                    {!isDigital && (
                                                        <p className="text-xs text-black dark:text-white/40 font-medium mt-1">{item.quantity} × ${item.unit_price.toFixed(2)}</p>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="mt-4 pt-4 border-t border-black/5 flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-bold uppercase tracking-widest text-black dark:text-white/50">Item Status:</span>
                                                    {getStatusBadge(item.status)}
                                                </div>

                                                {/* Fulfillment Actions (Only for physical items) */}
                                                {!isDigital && (
                                                    <div className="flex items-center gap-2">
                                                        {item.status === 'completed' ? (
                                                            <div className="flex items-center gap-1.5 text-xs font-bold text-green-600 uppercase tracking-widest">
                                                                <CheckCircle size={14} /> Fulfilled
                                                            </div>
                                                        ) : (
                                                            <>
                                                                {updatingParams === item.id && <Loader2 size={14} className="animate-spin text-jazz-brown" />}
                                                                <select
                                                                    value={item.status}
                                                                    onChange={(e) => handleUpdateItemStatus(item.id, e.target.value)}
                                                                    disabled={updatingParams === item.id}
                                                                    className="text-xs font-bold uppercase tracking-wider bg-white border border-black/10 rounded-lg px-2 py-1 outline-none"
                                                                >
                                                                    {item.status === "processing" && (
                                                                        <>
                                                                            <option value="processing" disabled>Processing</option>
                                                                            <option value="shipped">Mark Shipped</option>
                                                                        </>
                                                                    )}
                                                                    {item.status === "shipped" && (
                                                                        <>
                                                                            <option value="shipped" disabled>Shipped</option>
                                                                            <option value="completed">Mark Completed</option>
                                                                        </>
                                                                    )}
                                                                </select>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                                {isDigital && (
                                                    <div className="flex items-center gap-1.5 text-xs font-bold text-green-600 uppercase tracking-widest">
                                                        <CheckCircle size={14} /> Auto-Fulfilled
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>

                        <div className="mt-8 pt-6 border-t border-black/5 flex justify-between items-end">
                            <span className="text-xs font-bold uppercase tracking-[3px] text-black dark:text-white/50">Total Amount</span>
                            <span className="text-3xl font-bold font-serif text-black dark:text-white">${order.amount.toFixed(2)}</span>
                        </div>
                    </div>
                </div>

                {/* Right: Customer & Shipping */}
                <div className="space-y-6">
                    <div className="bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-black/5 shadow-sm">
                        <h2 className="text-lg font-bold mb-6 flex items-center gap-2" style={{ fontFamily: "Playfair Display, serif" }}>
                            <User size={20} className="text-jazz-brown" />
                            Customer Details
                        </h2>
                        <div className="space-y-4">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-widest text-black dark:text-white/50 mb-1">Name</p>
                                <p className="text-sm font-medium text-black dark:text-white">{order.customer.full_name}</p>
                            </div>
                            <div>
                                <p className="text-xs font-bold uppercase tracking-widest text-black dark:text-white/50 mb-1">Email</p>
                                <a href={`mailto:${order.customer.email}`} className="text-sm font-medium text-jazz-brown hover:underline">{order.customer.email}</a>
                            </div>
                        </div>
                    </div>

                    {hasPhysicalItems && (
                        <div className="bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-black/5 shadow-sm">
                            <h2 className="text-lg font-bold mb-6 flex items-center gap-2" style={{ fontFamily: "Playfair Display, serif" }}>
                                <MapPin size={20} className="text-jazz-brown" />
                                Shipping Address
                            </h2>
                            <div className="bg-[#FAFAFA] dark:bg-[#222] p-4 rounded-xl border border-black/5">
                                <p className="text-sm text-black dark:text-white/70 whitespace-pre-wrap leading-relaxed font-medium">
                                    {order.shipping_address || "No address provided."}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
